'use client';

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { X } from 'lucide-react';
import { ClientOnly } from '@/components/ClientOnly';

type AdminModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
};

function AdminModalPanel({
  open,
  onClose,
  title,
  description,
  children,
  size = 'lg',
  footer,
}: AdminModalProps) {
  const reduce = useReducedMotion();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const onCancel = (event: Event) => {
      event.preventDefault();
      onCloseRef.current();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      if (dialog.open) dialog.close();
    };
  }, [open]);

  if (!open) return null;

  const sizeClass =
    size === 'xl' ? 'ue-modal-panel-xl' : size === 'md' ? 'ue-modal-panel-md' : 'ue-modal-panel-lg';

  return createPortal(
    <dialog
      ref={dialogRef}
      className="ue-modal"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
    >
      <AnimatePresence>
        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="ue-modal-backdrop"
          onClick={onClose}
        />
      </AnimatePresence>
      <AnimatePresence>
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.99 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className={`ue-modal-panel ${sizeClass}`}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="ue-modal-header">
            <div className="min-w-0">
              <h2 id={titleId} className="ue-modal-title">{title}</h2>
              {description ? <p id={descriptionId} className="ue-modal-desc">{description}</p> : null}
            </div>
            <button type="button" onClick={onClose} className="ue-modal-close" aria-label="Fechar janela">
              <X className="w-5 h-5" />
            </button>
          </header>
          <div className="ue-modal-body">{children}</div>
          {footer ? <footer className="ue-modal-footer">{footer}</footer> : null}
        </motion.div>
      </AnimatePresence>
    </dialog>,
    document.body,
  );
}

export function AdminModal(props: AdminModalProps) {
  return (
    <ClientOnly>
      <AdminModalPanel {...props} />
    </ClientOnly>
  );
}
